import { Box, Button, Grid, Modal, Pagination, TextField, Typography } from "@mui/material"
import { useDebouncedValue } from "lib/@hooks/useDebouncedValue"
import { setFromEvent, setFromValueParam } from "lib/setFromEvent"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useEffect, useState } from "react"
import { getJobs } from "routes/sfg20-admin/plugins/system-tab/job-tab/controller/getJobs"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Repeat } from "lib/@components/repeat"

export function Jobs() {
    const [skip, setSkip] = useState(0)
    const take = 100
    const [searchTerm, setSearchTerm] = useState("")
    const [search, setSearch] = useDebouncedValue(searchTerm, 500, setSearchTerm)

    const jobs = getJobs.useResults({ searchTerm, skip, take })

    useEffect(() => setSkip(0), [search])
    return (
        <>
            {!jobs && <LoadingScreen />}
            {jobs && (
                <>
                    <TextField
                        fullWidth
                        value={search}
                        autoFocus
                        onChange={setFromEvent(setSearch)}
                        variant="standard"
                        label="Filter..."
                    />
                    <JobsTable
                        onChange={(p) => {
                            setSkip((p - 1) * take)
                        }}
                        jobs={jobs}
                        search={search}
                        take={take}
                    />
                </>
            )}
        </>
    )
}

export function JobsTable({ jobs, search, take, onChange }) {
    const variant = "outlined"
    const [page, setPage] = useState(0)
    const numberOfPages = Math.ceil(jobs.count / take)
    useEffect(() => setPage(0), [numberOfPages])
    return (
        <Grid container spacing={2} mt={2}>
            <ListItemBox>
                <Box flex={1} />
                <Pagination
                    variant={variant}
                    onChange={setFromValueParam((p) => {
                        setPage(p)
                        onChange(p)
                    })}
                    count={numberOfPages}
                    page={page}
                />
            </ListItemBox>
            <Grid container spacing={2} mt={2} ml={1}>
                <Grid item xs={2.5} md={2}>
                    Type
                </Grid>
                <Grid item xs={2}>
                    Config
                </Grid>
                <Grid item xs={1.5}>
                    Pause Until
                </Grid>
                <Grid item xs={1.5}>
                    Status
                </Grid>

                <Grid item xs={1.5}>
                    Attempts
                </Grid>
                <Grid item xs={1.5}>
                    Errors
                </Grid>
                <Grid item xs={1.5} md={2}>
                    TS
                </Grid>
            </Grid>
            <Repeat list={jobs.data} item={<JobItem />} pass="job" bind />
            <ListItemBox>
                <Box flex={1} />
                <Typography variant="body2">
                    {jobs.count} records found {search.length !== 0 && <>matching &quot;{search}&quot;</>}
                </Typography>
            </ListItemBox>
        </Grid>
    )
}

export function JobItem({ job }) {
    return (
        <Grid container spacing={2} mt={2} ml={1} key={job._id} sx={{ alignItems: "center" }}>
            <Grid item xs={2.5} md={2} data-cy="job-type-grid-item">
                {job.type}
            </Grid>
            <Grid item xs={2}>
                <BasicModal caption={"View Job"} jobId={job._id} content={JSON.stringify(job.job, null, 3)} />
            </Grid>
            <Grid item xs={1.5}>
                {job.pauseUntil}
            </Grid>
            <Grid item xs={1.5}>
                {job.status}
            </Grid>
            <Grid item xs={1.5}>
                {job.attempts}
            </Grid>
            <Grid item xs={1.5}>
                {job.errors.length !== 0 && (
                    <BasicModal
                        caption={`View ${job.errors.length} Errors`}
                        jobId={job._id}
                        content={JSON.stringify(job.errors, null, 3)}
                    />
                )}
                {job.errors.length === 0 && <>No</>}
            </Grid>
            <Grid item xs={1.5} md={2}>
                {new Date(job._created).toLocaleString("en-UK")}
            </Grid>
        </Grid>
    )
}

export default function BasicModal({ caption, jobId, content }) {
    const [open, setOpen] = useState(false)
    const handleOpen = () => setOpen(true)
    const handleClose = () => setOpen(false)

    const style = {
        position: "absolute",
        top: 50,
        left: 50,
        right: 50,
        bottom: 50,
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4,
        overflow: "scroll",
    }

    return (
        <>
            <Button onClick={handleOpen}>{caption}</Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        {jobId}
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                        <pre>{content}</pre>
                    </Typography>
                </Box>
            </Modal>
        </>
    )
}
